import { Grid, Stack, Switch, Typography, IconButton, Box, Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import * as React from "react";
import { useCallback, useState } from "react";
import { FireServices } from "../../../../../shared/services/api/fire/FireServices";
import { globalRemoveMaskReal2 } from "../../../../../shared/utils";
import { useToast } from "../../../../../shared/hooks/useToast";
import { MaskField } from "./MaskField";
import { CoveragerRowUpdate } from "../../../../../shared/services/api/fire/types";
import { HtmlTooltip } from "../../../../../infra/components/tooltipsCustom/tooltipsCuston";

// Tipo para os valores da cobertura que serão rastreados
type CoverageValues = {
	enabled: boolean;
	minValue: string;
	maxValue: string;
	factor: string;
	rateIncrease: string;
};

export interface CoverageItemFieldsProps {
	id: string | number;
	namePrefix?: string;
	label: string;
	description?: string;
	minValue?: string;
	maxValue?: string;
	factor?: string;
	rateIncrease?: string;
	enabled?: boolean;
	disabled?: boolean;
}

export const CoverageItemFields = React.memo(
	({
		id,
		namePrefix = `coverage-${id}`,
		label,
		description,
		minValue: initialMinValue = "",
		maxValue: initialMaxValue = "",
		factor: initialFactor = "",
		rateIncrease: initialRateIncrease = "",
		enabled: initialEnabled = false,
		disabled = false,
	}: CoverageItemFieldsProps) => {
		// Tipagem incluída na função interna
		const coverageLabel = label || description;
		const toastHandler = useToast();
		const [isSaving, setIsSaving] = useState(false);

		const initialPropsValues: CoverageValues = React.useMemo(
			() => ({
				enabled: initialEnabled,
				minValue: initialMinValue,
				maxValue: initialMaxValue,
				factor: initialFactor,
				rateIncrease: initialRateIncrease,
			}),
			[initialEnabled, initialMinValue, initialMaxValue, initialFactor, initialRateIncrease]
		);

		const [currentValues, setCurrentValues] = useState<CoverageValues>(initialPropsValues);
		const [lastSavedValues, setLastSavedValues] = useState<CoverageValues>(initialPropsValues);

		const isDirty =
			currentValues.enabled !== lastSavedValues.enabled ||
			currentValues.minValue !== lastSavedValues.minValue ||
			currentValues.maxValue !== lastSavedValues.maxValue ||
			currentValues.factor !== lastSavedValues.factor ||
			currentValues.rateIncrease !== lastSavedValues.rateIncrease;

		const isInputDisabled = disabled || !currentValues.enabled;

		// HANDLERS
		const handleInputChange = useCallback(
			(field: keyof CoverageValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
				setCurrentValues((prev) => ({ ...prev, [field]: event.target.value }));
			},
			[]
		);

		const handleToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
			setCurrentValues((prev) => ({ ...prev, enabled: event.target.checked }));
		}, []);

		const handleConfirm = useCallback(async () => {
			if (!isDirty) return;

			setIsSaving(true);

			const finalPayload: CoveragerRowUpdate = {
				limite_min: globalRemoveMaskReal2(currentValues.minValue),
				limite_max: globalRemoveMaskReal2(currentValues.maxValue),
				factor: String(currentValues.factor || "0"),
				rate_increase: globalRemoveMaskReal2(currentValues.rateIncrease),
				status: currentValues.enabled ? "active" : "inactive",
			};

			try {
				const result = await FireServices.updateClientFireCoverages(String(id), finalPayload);

				if (result instanceof Error) {
					throw result;
				}

				toastHandler.present({
					type: "success",
					messageOrContent: `Cobertura ${label} atualizada com sucesso!`,
					position: "top-right",
				});

				// ATUALIZA O ESTADO DE VALORES SALVOS PARA SUMIR COM O BOTÃO
				setLastSavedValues(currentValues);
			} catch (error) {
				console.error(`Erro ao salvar cobertura ${id}:`, error);
				toastHandler.present({
					type: "error",
					messageOrContent: `Falha ao atualizar ${label}. Verifique o console.`,
					position: "top-right",
				});
			} finally {
				setIsSaving(false);
			}
		}, [id, isDirty, currentValues, label, toastHandler]);

		return (
			<Grid
				container
				spacing={1}
				alignItems="flex-start"
				sx={{
					py: 1,
				}}
			>
				<Grid item xs={12} sm={4} md={3} sx={{ display: "flex", alignItems: "center" }}>
					<Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: "100%" }}>
						<Switch color="secondary" size="medium" checked={currentValues.enabled} onChange={handleToggle} disabled={disabled || isSaving} />

						<HtmlTooltip title={`${coverageLabel}`} arrow placement="top">
							<Typography
								variant="body2"
								sx={{
									fontWeight: 500,
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									minWidth: 0,
									cursor: "default",
									maxWidth: "100%",
								}}
							>
								{coverageLabel}
							</Typography>
						</HtmlTooltip>
					</Stack>
				</Grid>

				{/* Limite Mínimo - md={2} */}
				<Grid item xs={12} sm={4} md={2}>
					<MaskField
						name={`${namePrefix}-minValue`}
						label="Limite Mínimo *"
						mask="real"
						disabled={isInputDisabled || isSaving}
						value={currentValues.minValue}
						onChange={handleInputChange("minValue")}
					/>
				</Grid>

				{/* Limite Máximo - md={2} */}
				<Grid item xs={12} sm={4} md={2}>
					<MaskField
						name={`${namePrefix}-maxValue`}
						label="Limite Máximo *"
						mask="real"
						disabled={isInputDisabled || isSaving}
						value={currentValues.maxValue}
						onChange={handleInputChange("maxValue")}
					/>
				</Grid>

				{/* FATOR: AJUSTE DE TAMANHO para md={1.5} */}
				<Grid item xs={12} sm={4} md={1.5}>
					<MaskField
						name={`${namePrefix}-factor`}
						label="Fator *"
						mask="inteiro"
						disabled={isInputDisabled || isSaving}
						value={currentValues.factor}
						onChange={handleInputChange("factor")}
					/>
				</Grid>

				<Grid item xs={12} sm={4} md={3.2}>
					<Stack direction="row" alignItems="flex-start" spacing={1}>
						<Box sx={{ flexGrow: 1 }}>
							<MaskField
								name={`${namePrefix}-rateIncrease`}
								label="Agravamento da taxa *"
								mask="percet"
								disabled={isInputDisabled || isSaving}
								value={currentValues.rateIncrease}
								onChange={handleInputChange("rateIncrease")}
							/>
						</Box>

						{/* ICON BUTTON DE CONFIRMAÇÃO */}
						{isDirty && (
							<Tooltip title="Confirmar alterações" arrow placement="top">
								<IconButton
									color="secondary"
									onClick={handleConfirm}
									disabled={disabled || isSaving}
									aria-label={`Confirmar alterações para ${label}`}
									size="large"
									sx={{
										mt: 0.5,
										p: 1,
									}}
								>
									<CheckIcon />
								</IconButton>
							</Tooltip>
						)}
					</Stack>
				</Grid>
			</Grid>
		);
	}
);
